import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Row, Col, Button, message, Table, Tag } from "antd";
import dayjs from "dayjs";
import {
   FileExcelOutlined, CalendarOutlined, DollarOutlined,
   TrophyOutlined, BarChartOutlined, DownloadOutlined
} from "@ant-design/icons";
import { getReport, exportReport } from "../../services/reportsApi";

// ─── Constants ───
const REPORT_TYPES = [
   { key: "daily", label: "Daily", icon: <CalendarOutlined /> },
   { key: "weekly", label: "Weekly", icon: <CalendarOutlined /> },
   { key: "monthly", label: "Monthly", icon: <CalendarOutlined /> }
];

const STATUS_COLORS = {
   "Closed Won": "green",
   "Closed Lost": "red",
   default: "blue"
};

// ─── Styles ───
const styles = `
   .reports-card {
      background: #fff;
      border-radius: 10px;
      box-shadow: 0 1px 2px 0 rgba(0,0,0,0.03);
      border: 1px solid #f3f4f6;
   }
   .reports-table .ant-table-thead > tr > th {
      background: #f9fafb;
      font-size: 12px;
      font-weight: 600;
      padding: 10px 16px;
   }
   .reports-table .ant-table-tbody > tr > td {
      padding: 10px 16px;
      font-size: 13px;
   }
   .reports-table .ant-table-tbody > tr:hover > td {
      background: #f9fafb;
   }
   .stat-card {
      border-radius: 10px;
      transition: all 0.3s;
   }
   .stat-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
   }
`;

// ─── Helper Functions ───
const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.default;

const formatCurrency = (amount) => `₹ ${Number(amount).toLocaleString('en-IN')}`;

// ─── Main Component ───
const Reports = () => {
   const [type, setType] = useState("weekly");
   const [summary, setSummary] = useState(null);
   const [rows, setRows] = useState([]);
   const [loading, setLoading] = useState(false);
   const [exportLoading, setExportLoading] = useState(false);

   // ─── Fetch Report ───
   const fetchReport = useCallback(async (reportType) => {
      setType(reportType);
      setLoading(true);

      try {
         const { data } = await getReport(reportType);
         setSummary(data.summary);
         setRows(data.rows);
      } catch (error) {
         message.error("Failed to load report");
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      fetchReport("weekly");
   }, [fetchReport]);

   // ─── Export Handler ───
   const handleExport = async () => {
      setExportLoading(true);
      try {
         const response = await exportReport(type);

         // Create download link
         const url = window.URL.createObjectURL(new Blob([response.data]));
         const link = document.createElement("a");
         link.href = url;
         link.setAttribute("download", `${type}-report-${dayjs().format('YYYY-MM-DD')}.xlsx`);
         document.body.appendChild(link);
         link.click();
         link.remove();
         window.URL.revokeObjectURL(url);

         message.success("Export successful");
      } catch (error) {
         message.error("Export failed");
      } finally {
         setExportLoading(false);
      }
   };

   // ─── Report Title ───
   const reportTitle = useMemo(() => {
      const typeLabel = REPORT_TYPES.find(t => t.key === type)?.label || "";
      return `${typeLabel} Detailed Report`;
   }, [type]);

   // ─── Table Columns ───
   const columns = [
      {
         title: "#",
         width: 50,
         align: "center",
         render: (_, __, index) => (
            <span className="text-xs text-gray-400">{index + 1}</span>
         )
      },
      {
         title: "Lead Name",
         dataIndex: "lead_name",
         render: (name) => (
            <span className="font-medium text-gray-800">{name}</span>
         )
      },
      {
         title: "Status",
         dataIndex: "status",
         render: (status) => (
            <Tag color={getStatusColor(status)}>{status}</Tag>
         )
      },
      {
         title: "Sale Amount",
         dataIndex: "sale_amount",
         render: (val) => val ? (
            <span className="font-semibold text-green-600">
               {formatCurrency(val)}
            </span>
         ) : (
            <span className="text-gray-400">-</span>
         )
      },
      {
         title: "Closing Date",
         dataIndex: "closing_date",
         render: (date) => (
            <span className="text-gray-600 text-sm">
               {date ? dayjs(date).format("DD MMM YYYY") : "-"}
            </span>
         )
      }
   ];

   return (
      <div className="bg-gray-50 min-h-screen">
         <style>{styles}</style>

         {/* Header */}
         <ReportHeader
            type={type}
            onTypeChange={fetchReport}
            onExport={handleExport}
            exportLoading={exportLoading}
            hasData={!!summary}
         />

         <div className="p-6">
            {/* Stats Cards */}
            {summary && (
               <StatsSection summary={summary} />
            )}

            {/* Table Card */}
            <div className="reports-card reports-table">
               <div className="p-4 border-b">
                  <div className="flex items-center gap-2">
                     <FileExcelOutlined className="text-gray-600" />
                     <span className="font-medium text-gray-700">
                        {reportTitle}
                     </span>
                     <Tag>{rows.length}</Tag>
                  </div>
               </div>

               <Table
                  size="small"
                  columns={columns}
                  dataSource={rows}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                     pageSize: 10,
                     showTotal: (total, range) => (
                        <span className="text-sm text-gray-500">
                           Showing <b>{range[0]}-{range[1]}</b> of <b>{total}</b> records
                        </span>
                     ),
                     size: "small"
                  }}
                  locale={{
                     emptyText: loading ? "Loading..." : "No data available"
                  }}
               />
            </div>
         </div>
      </div>
   );
};

// ─── Sub Components ───
const ReportHeader = ({ type, onTypeChange, onExport, exportLoading, hasData }) => (
   <div className="bg-white border-b px-6 py-4">
      <div className="flex items-center justify-between">
         <div>
            <h1 className="text-xl font-semibold text-gray-800 m-0">Reports</h1>
            <span className="text-gray-400 text-sm">Sales conversion reports</span>
         </div>
         <div className="flex gap-2">
            {REPORT_TYPES.map(reportType => (
               <Button
                  key={reportType.key}
                  type={type === reportType.key ? "primary" : "default"}
                  icon={reportType.icon}
                  onClick={() => onTypeChange(reportType.key)}
               >
                  {reportType.label}
               </Button>
            ))}
            <Button
               icon={<DownloadOutlined />}
               onClick={onExport}
               loading={exportLoading}
               disabled={!hasData}
            >
               Export Excel
            </Button>
         </div>
      </div>
   </div>
);

const StatsSection = ({ summary }) => (
   <Row gutter={[16, 16]} className="mb-4">
      <Col xs={24} sm={8}>
         <StatCard
            title="Total Leads"
            value={summary.totalLeads}
            icon={<BarChartOutlined />}
            color="blue"
         />
      </Col>

      <Col xs={24} sm={8}>
         <StatCard
            title="Converted Leads"
            value={summary.convertedLeads}
            icon={<TrophyOutlined />}
            color="green"
         />
      </Col>

      <Col xs={24} sm={8}>
         <StatCard
            title="Total Revenue"
            value={formatCurrency(summary.totalRevenue)}
            icon={<DollarOutlined />}
            color="green"
            valueColor="text-green-600"
         />
      </Col>
   </Row>
);

const StatCard = ({ title, value, icon, color, valueColor = "text-gray-800" }) => (
   <div className="reports-card stat-card p-4">
      <div className="flex items-center justify-between">
         <div>
            <div className="text-xs text-gray-500 mb-1">{title}</div>
            <div className={`text-2xl font-bold ${valueColor}`}>
               {value}
            </div>
         </div>
         <div className={`w-10 h-10 rounded-lg bg-${color}-100 flex items-center justify-center`}>
            <span className={`text-${color}-500 text-lg`}>{icon}</span>
         </div>
      </div>
   </div>
);

export default Reports;